//© 2025 Kaitlyn Troxell
import React, { useState } from 'react';
import LoadingSpinner from './LoadingSpinner';

function ProjectManager({ 
  currentProject, 
  projects, 
  onSelectProject, 
  onCreateProject, 
  onDeleteProject,
  setShowProjectManager 
}) {
  const [newProjectName, setNewProjectName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [selectedSections, setSelectedSections] = useState([]);

  const availableSections = [
    { id: 'exterior-siding', title: '🏠 Exterior - Siding' },
    { id: 'exterior-roof', title: '🏠 Exterior - Roof' },
    { id: 'exterior-garage', title: '🚗 Exterior - Garage' },
    { id: 'exterior-brick', title: '🧱 Exterior - Brick/Stone' },
    { id: 'exterior-concrete', title: '🛤️ Exterior - Concrete' },
    { id: 'exterior-deck', title: '♨️ Exterior - Deck' },
    { id: 'exterior-landscape', title: '🌱 Exterior - Landscaping' },
    { id: 'interior-fireplace', title: '🔥 Interior - Brick/Stone' },
    { id: 'interior-windows', title: '🚪 Interior - Windows/Doors' },
    { id: 'interior-basement', title: '🏚️ Interior - Basement' },
    { id: 'interior-stairs', title: '↗️ Interior - Stairs' },
    { id: 'interior-insulation', title: '🧊 Interior - Insulation' },
    { id: 'interior-mechanicals', title: '🔧 Interior - Mechanicals' },
    { id: 'interior-appliances', title: '📺 Interior - Appliances' },
    { id: 'interior-flooring', title: '🏠 Interior - Flooring' },
    { id: 'interior-tile', title: '🎨 Interior - Tile & Grout' },
    { id: 'interior-bathroom', title: '🚿 Interior - Bathroom' },
    { id: 'interior-kitchen', title: '🍳 Interior - Kitchen' },
    { id: 'interior-cabinets', title: '🗄️ Interior - Cabinets' },
    { id: 'interior-walls', title: '🎨 Interior - Walls' }
  ];

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      setError('Project name is required');
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating project and copying tasks...');
    setError('');

    try {
      await onCreateProject(newProjectName.trim());
      setNewProjectName('');
      setShowCreateForm(false);
    } catch (err) {
      console.error('Create project error:', err);
      setError('Failed to create project: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project? This will also delete all associated tasks and calendar events.')) {
      setLoading(true);
      setLoadingMessage('Deleting project...');
      setError('');

      try {
        await onDeleteProject(projectId);
      } catch (err) {
        console.error('Delete project error:', err);
        setError('Failed to delete project: ' + (err.message || 'Unknown error'));
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    }
  };

  return (
    <>
      {loading && <LoadingSpinner message={loadingMessage} />}
      
      <div className="modal-backdrop">
        <div className="modal-container project-manager-modal">
          <h3 className="modal-title">Project Manager</h3>
          
          {error && (
            <div className="form-error">{error}</div>
          )}

          <div className="current-project-info">
            <strong>Current Project:</strong> {currentProject?.name || 'None'}
          </div>

          <div className="projects-list">
            <h4 className="modal-subtitle">All Projects</h4>
            {projects.length === 0 ? (
              <div className="empty-state">No projects yet. Create your first one below.</div>
            ) : (
              projects.map(project => (
                <div key={project.id} className="project-item">
                  <div className="project-item-content">
                    <span className="project-name">{project.name}</span>
                    <span className="project-date">
                      {project.created_at && project.created_at.toDate && (
                        `Created: ${project.created_at.toDate().toLocaleDateString()}`
                      )}
                    </span>
                  </div>
                  <div className="project-item-actions">
                    <button
                      onClick={() => {
                        onSelectProject(project.id);
                        setShowProjectManager(false);
                      }}
                      className={`select-project-btn ${currentProject?.id === project.id ? 'active' : ''}`}
                    >
                      {currentProject?.id === project.id ? '✓ Active' : 'Select'}
                    </button>
                    {projects.length > 1 && (
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        className="delete-project-btn"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {!showCreateForm ? (
            <button
              onClick={() => setShowCreateForm(true)}
              className="add-contractor-btn"
              style={{ marginTop: '20px' }}
            >
              + Create New Project
            </button>
          ) : (
            <div className="create-project-form">
              <div className="form-group">
                <label className="form-label" htmlFor="projectName">
                  New Project Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="projectName"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="form-input"
                  placeholder="e.g., 123 Main Street Remodel"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateProject();
                    }
                  }}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewProjectName('');
                    setError('');
                  }}
                  className="modal-cancel-btn"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  onClick={handleCreateProject} 
                  className="save-btn"
                  disabled={loading}
                >
                  Create Project
                </button>
              </div>
            </div>
          )}

          <div className="modal-actions" style={{ marginTop: '20px' }}>
            <button
              onClick={() => setShowProjectManager(false)}
              className="modal-cancel-btn"
              disabled={loading}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ProjectManager;